import type { Artifact, MessageArtifact } from '@ideoz/core';
import { composeArtifactTitle } from '@ideoz/core';

const humaniseName = (raw: string | null | undefined): string => {
  if (!raw) return 'Artifact';
  const trimmed = raw.replace(/[_-]+/g, ' ').trim();
  if (!trimmed) return raw;
  return trimmed
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Pull a readable title out of the artifact. Prefers the typed title block
 * ("artifact_name - artifact_type - version") from `structured_content`; then
 * the first markdown heading in the content ("# Something"); falls back to the
 * row's artifact_type. Keeps the list-item label and the panel title consistent.
 */
export const artifactTitle = (row: Artifact): string => {
  const composed = composeArtifactTitle(row.structured_content);
  if (composed) return composed;
  const content = row.content ?? '';
  const match = content.match(/^\s*#{1,6}\s+(.+?)\s*$/m);
  if (match?.[1]) return match[1].trim();
  return humaniseName(row.artifact_type);
};

export const asMessageArtifact = (row: Artifact): MessageArtifact => ({
  id: row.id,
  name: artifactTitle(row),
  content: typeof row.content === 'string' ? row.content : '',
  format: row.structured_content ? 'json' : 'markdown',
  structuredContent: row.structured_content ?? null,
  superAgentId: null,
  sourceToolId: null,
  demoUrl: row.demo_url ?? null,
  fileFormat: row.file_format ?? null,
  fileName: row.file_name ?? null,
  mediaType: row.media_type ?? null,
  fileStatus: row.file_status ?? null,
});
